/**
 * Performance mensal das carteiras consolidadas no Warren One.
 *
 * Na URL: mês, mercado (onshore/offshore), officers mostrados, critérios de marcação,
 * officers e carteiras excluídos do cálculo (painel de exclusões; clicar numa linha da
 * tabela também exclui a carteira, com desfazer), o agrupamento do resumo e, nos grupos de pares (só onshore), o
 * método (Ward ou faixas), as variáveis, o número de grupos, os limites e o eixo do
 * gráfico. O histórico de todas as carteiras e as características de alocação são
 * carregados uma vez por dia em segundo plano (~1 min); depois disso, trocar mês ou
 * filtro responde na hora.
 */
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Paper,
  Badge,
  CloseButton,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { Cronometro } from '../../components/Cronometro';
import { BarraFiltros, CampoOpcoes, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { Kpi, type KpiApi } from '../../components/Kpi';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { formatarDataHora } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/performance';
export const INTERVALO_CONSULTA = 3000;

type Agrupar = 'cluster' | 'officer' | 'marcacao';

export const VARIAVEIS = [
  { valor: 'caixa_pos', rotulo: 'Caixa e Pós' },
  { valor: 'pre', rotulo: 'Pré' },
  { valor: 'inflacao', rotulo: 'Inflação' },
  { valor: 'rv', rotulo: 'Renda Variável' },
  { valor: 'alternativos', rotulo: 'Alternativos' },
  { valor: 'duration', rotulo: 'Duration' },
];
const TODAS_VARIAVEIS = VARIAVEIS.map((v) => v.valor);
const OPCOES_VARIAVEIS = VARIAVEIS.map((v) => ({ value: v.valor, label: v.rotulo }));
const NOME_AGRUPAMENTO: Record<Agrupar, string> = {
  cluster: 'grupo de pares',
  officer: 'officer',
  marcacao: 'marcação',
};

interface Resultado {
  mes: string | null;
  mercado: string;
  agrupar: Agrupar;
  caracteristicas: boolean;
  status_caracteristicas: 'rodando' | 'pronto' | 'erro' | null;
  opcoes: {
    meses: { valor: string; rotulo: string }[];
    officers: string[];
    marcacoes: string[];
    carteiras: { valor: string; rotulo: string }[];
  };
  exclusoes: {
    total: number;
    carteiras: { valor: string; rotulo: string }[];
    officers: { officer: string; n: number }[];
  };
  agrupamento?: {
    n_universo: number;
    n_grupos: number;
    silhueta: number | null;
    sugestao: { grupos: number; silhueta: number } | null;
  };
  n: number;
  aviso?: string;
  parcial?: boolean;
  benchmarks?: { caixa: string | null; risco: string | null };
  kpis?: KpiApi[];
  resumo?: TabelaApi;
  grafico?: GraficoApi;
  tabela?: TabelaApi;
}

interface Estado {
  status: 'rodando' | 'pronto' | 'erro';
  iniciado_em: string;
  mensagem?: string;
  resultado?: Resultado;
}

const lista = (texto: string) => (texto ? texto.split('|') : []);
const decimal = (v: number) => v.toFixed(2).replace('.', ',');

function legendaResumo(r: Resultado) {
  const base =
    "Retornos em %, pesos iguais; colunas 'Acima do' contam carteiras que superaram o benchmark.";
  if (r.agrupar !== 'cluster') return base;
  const a = r.agrupamento;
  const universo = a
    ? `${a.n_grupos} grupos formados com ${a.n_universo} carteiras (onshore do mês, sem as excluídas e só das marcações filtradas); o filtro de officer só limita o que aparece, e "No grupo" conta o grupo inteiro.`
    : '';
  const partes = [
    base,
    universo,
    'Perfil médio do grupo inteiro em % do PL e duration em anos, pela alocação atual (snapshot do Fibery), não a do início do mês.',
  ].filter(Boolean);
  if (a?.silhueta != null) {
    const sugestao = a.sugestao
      ? ` (a maior entre 2 e 10 grupos é ${decimal(a.sugestao.silhueta)}, com ${a.sugestao.grupos})`
      : '';
    partes.push(
      `Silhueta ${decimal(a.silhueta)}${sugestao}: de −1 a 1, quanto maior, mais separados os grupos.`,
    );
  }
  return partes.join(' ');
}

/** Limites das faixas: vale ao sair do campo ou no Enter (como as datas). */
function CampoLimites({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }) {
  const confirmar = (texto: string) => texto.trim() !== valor && aoMudar(texto.trim());
  return (
    <TextInput
      key={valor}
      label="Limites"
      placeholder="Quantis"
      description="Ex.: 10, 25"
      defaultValue={valor}
      onBlur={(e) => confirmar(e.currentTarget.value)}
      onKeyDown={(e) => e.key === 'Enter' && confirmar(e.currentTarget.value)}
      w={LARGURA.texto}
    />
  );
}

interface Ultima {
  id: string;
  nome: string;
}

function contagem(n: number, singular: string, plural: string) {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** Etiqueta de uma exclusão; o × (um botão de verdade, acessível pelo teclado) reinclui. */
function Etiqueta({
  rotulo,
  reincluir,
  aoRemover,
}: {
  rotulo: string;
  reincluir: string;
  aoRemover: () => void;
}) {
  return (
    <Badge
      variant="light"
      color="gray"
      size="lg"
      tt="none"
      fw={500}
      pr={4}
      rightSection={<CloseButton size="xs" aria-label={reincluir} onClick={aoRemover} />}
    >
      {rotulo}
    </Badge>
  );
}

/** Exclusões do cálculo: officers inteiros e carteiras, com etiquetas para desfazer. */
function PainelExclusoes({
  r,
  officers,
  carteiras,
  definir,
  ultima,
  desfazer,
}: {
  r: Resultado | undefined;
  officers: string[];
  carteiras: string[];
  definir: (mudancas: { excluir_officers?: string; excluir?: string }) => void;
  ultima: Ultima | null;
  desfazer: () => void;
}) {
  const ex = r?.exclusoes;
  const total = ex?.total ?? 0;
  const algum = officers.length + carteiras.length > 0;
  const [aberto, setAberto] = useState(algum);
  const nomes = new Map((r?.opcoes.carteiras ?? []).map((c) => [c.valor, c.rotulo]));
  const porOfficer = (ex?.officers ?? []).reduce((soma, o) => soma + o.n, 0);
  return (
    <Paper withBorder radius="md" p="sm">
      <Group justify="space-between" align="center" gap="sm">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          Exclusões do cálculo
        </Text>
        <Group gap="sm">
          <Text size="sm" c="dimmed">
            {total
              ? `${contagem(total, 'carteira fora do cálculo', 'carteiras fora do cálculo')} (${porOfficer} de officers excluídos, ${ex?.carteiras.length ?? 0} individuais)`
              : 'Nenhuma carteira excluída.'}
          </Text>
          {algum ? (
            <Button
              size="compact-sm"
              variant="subtle"
              onClick={() => definir({ excluir_officers: '', excluir: '' })}
            >
              Limpar exclusões
            </Button>
          ) : (
            !aberto && (
              <Button size="compact-sm" variant="subtle" onClick={() => setAberto(true)}>
                Excluir officers ou carteiras
              </Button>
            )
          )}
        </Group>
      </Group>
      {(aberto || algum) && (
        <Stack gap="xs" mt="xs">
          <Group gap="sm" align="flex-start">
            <MultiSelect
              label="Officers"
              description="Todas as carteiras do officer saem do cálculo e dos grupos"
              placeholder={officers.length ? '' : 'Nenhum'}
              data={r?.opcoes.officers ?? []}
              value={officers}
              onChange={(v) => definir({ excluir_officers: v.join('|') })}
              searchable
              clearable
              w={LARGURA.selecao + 80}
            />
            <MultiSelect
              label="Carteiras"
              description="Ou clique numa linha da tabela Carteiras"
              placeholder={carteiras.length ? '' : 'Nenhuma'}
              data={(r?.opcoes.carteiras ?? []).map((c) => ({ value: c.valor, label: c.rotulo }))}
              value={carteiras}
              onChange={(v) => definir({ excluir: v.join('|') })}
              searchable
              clearable
              w={LARGURA.selecao + 80}
            />
          </Group>
          {algum && (
            <Group gap={6}>
              {officers.map((o) => {
                const n = ex?.officers.find((x) => x.officer === o)?.n;
                return (
                  <Etiqueta
                    key={`o:${o}`}
                    rotulo={`Officer: ${o}${n != null ? ` (${n})` : ''}`}
                    reincluir={`Reincluir officer ${o}`}
                    aoRemover={() =>
                      definir({ excluir_officers: officers.filter((x) => x !== o).join('|') })
                    }
                  />
                );
              })}
              {carteiras.map((id) => (
                <Etiqueta
                  key={`c:${id}`}
                  rotulo={nomes.get(id) ?? id}
                  reincluir={`Reincluir ${nomes.get(id) ?? id}`}
                  aoRemover={() =>
                    definir({ excluir: carteiras.filter((x) => x !== id).join('|') })
                  }
                />
              ))}
            </Group>
          )}
          {ultima && (
            <Text size="sm">
              {ultima.nome} excluída do cálculo ·{' '}
              <Anchor component="button" type="button" size="sm" onClick={desfazer}>
                Desfazer
              </Anchor>
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
}

export function Performance() {
  const [f, definir] = useFiltrosUrl({
    mes: '',
    mercado: 'onshore',
    officers: '',
    marcacoes: '',
    excluir: '',
    excluir_officers: '',
    agrupar: '',
    metodo: 'ward',
    variaveis: '',
    variavel: 'rv',
    grupos: 6,
    limites: '',
    eixo: 'rv',
  });
  const onshore = f.mercado === 'onshore';
  const agrupar = (f.agrupar || (onshore ? 'cluster' : 'officer')) as Agrupar;
  const pares = onshore && agrupar === 'cluster';
  const consulta = useQuery({
    queryKey: ['carteiras.performance', f],
    queryFn: ({ signal }) =>
      apiGet<Envelope<Estado>>(
        CAMINHO,
        {
          mes: f.mes || undefined,
          mercado: f.mercado,
          officers: lista(f.officers),
          marcacoes: lista(f.marcacoes),
          excluir: lista(f.excluir),
          excluir_officers: lista(f.excluir_officers),
          agrupar: f.agrupar || undefined,
          ...(pares && {
            metodo: f.metodo,
            variaveis: f.metodo === 'ward' ? lista(f.variaveis) : [],
            variavel: f.metodo === 'faixas' ? f.variavel : undefined,
            grupos: f.grupos,
            limites: f.metodo === 'faixas' ? f.limites || undefined : undefined,
            eixo: f.eixo,
          }),
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: true,
    refetchInterval: (q) => {
      const data = q.state.data?.data;
      const carregando =
        data?.status === 'rodando' || data?.resultado?.status_caracteristicas === 'rodando';
      return carregando ? INTERVALO_CONSULTA : false;
    },
  });
  const estado = consulta.data?.data;
  const r = estado?.status === 'pronto' ? estado.resultado : undefined;
  const agrupamentoMostrado = r?.agrupar ?? agrupar;
  const variaveis = lista(f.variaveis).length ? lista(f.variaveis) : TODAS_VARIAVEIS;
  const rotuloEixo = VARIAVEIS.find((v) => v.valor === f.eixo)?.rotulo ?? '';
  const [ultima, setUltima] = useState<Ultima | null>(null);
  const excluidas = lista(f.excluir);

  function excluirPelaTabela(linha: Record<string, unknown>) {
    const id = typeof linha.id === 'string' ? linha.id : null;
    if (!id || excluidas.includes(id)) return;
    const nome = r?.opcoes.carteiras.find((c) => c.valor === id)?.rotulo ?? id;
    definir({ excluir: [...excluidas, id].join('|') });
    setUltima({ id, nome });
  }

  function desfazer() {
    if (!ultima) return;
    definir({ excluir: excluidas.filter((x) => x !== ultima.id).join('|') });
    setUltima(null);
  }

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Performance"
        descricao="Rentabilidade das carteiras consolidadas no mês, com pesos iguais. Onshore contra CDI e Ibovespa, comparada entre grupos de pares formados pela alocação; offshore, em USD, contra o S&P 500."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Warren One · Fibery (officer, critério de marcação, posições)"
      />
      <BarraFiltros carregando={consulta.isFetching && estado?.status !== 'rodando'}>
        <GrupoFiltros titulo="Período">
          <Select
            label="Mês"
            data={(r?.opcoes.meses ?? []).map((m) => ({ value: m.valor, label: m.rotulo }))}
            value={r?.mes ?? (f.mes || null)}
            onChange={(mes) => mes && definir({ mes })}
            disabled={!r}
            w={LARGURA.data}
          />
          <CampoOpcoes
            rotulo="Mercado"
            valor={f.mercado}
            aoMudar={(mercado) =>
              definir({
                mercado,
                officers: '',
                marcacoes: '',
                excluir: '',
                excluir_officers: '',
                agrupar: '',
              })
            }
            opcoes={[
              { valor: 'onshore', rotulo: 'Onshore' },
              { valor: 'offshore', rotulo: 'Offshore' },
            ]}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Carteiras">
          <MultiSelect
            label="Mostrar officers"
            placeholder={f.officers ? '' : 'Todos'}
            data={r?.opcoes.officers ?? []}
            value={lista(f.officers)}
            onChange={(v) => definir({ officers: v.join('|') })}
            searchable
            clearable
            w={LARGURA.selecao}
          />
          <MultiSelect
            label="Marcação"
            placeholder={f.marcacoes ? '' : 'Todas'}
            data={r?.opcoes.marcacoes ?? []}
            value={lista(f.marcacoes)}
            onChange={(v) => definir({ marcacoes: v.join('|') })}
            clearable
            w={LARGURA.selecao}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Resumo">
          <CampoOpcoes
            rotulo="Agrupar por"
            valor={agrupar}
            aoMudar={(v) => definir({ agrupar: v === (onshore ? 'cluster' : 'officer') ? '' : v })}
            opcoes={[
              ...(onshore ? [{ valor: 'cluster', rotulo: 'Grupos de pares' }] : []),
              { valor: 'officer', rotulo: 'Officer' },
              { valor: 'marcacao', rotulo: 'Marcação' },
            ]}
          />
        </GrupoFiltros>
        {pares && (
          <GrupoFiltros titulo="Grupos de pares">
            <CampoOpcoes
              rotulo="Método"
              valor={f.metodo}
              aoMudar={(metodo) => definir({ metodo })}
              opcoes={[
                { valor: 'ward', rotulo: 'Ward' },
                { valor: 'faixas', rotulo: 'Faixas' },
              ]}
            />
            {f.metodo === 'ward' ? (
              <MultiSelect
                label="Variáveis"
                data={OPCOES_VARIAVEIS}
                value={variaveis}
                onChange={(v) => {
                  if (!v.length) return; // ao menos uma variável
                  const todas = TODAS_VARIAVEIS.every((x) => v.includes(x));
                  definir({ variaveis: todas ? '' : v.join('|') });
                }}
                w={LARGURA.selecao}
              />
            ) : (
              <>
                <Select
                  label="Variável"
                  data={OPCOES_VARIAVEIS}
                  value={f.variavel}
                  onChange={(variavel) => variavel && definir({ variavel })}
                  allowDeselect={false}
                  w={LARGURA.selecao}
                />
                <CampoLimites valor={f.limites} aoMudar={(limites) => definir({ limites })} />
              </>
            )}
            <NumberInput
              label="Grupos"
              min={2}
              max={10}
              value={f.grupos}
              disabled={f.metodo === 'faixas' && Boolean(f.limites)}
              onChange={(v) => typeof v === 'number' && v >= 2 && v <= 10 && definir({ grupos: v })}
              w={LARGURA.numero}
            />
            <Select
              label="Eixo do gráfico"
              data={OPCOES_VARIAVEIS}
              value={f.eixo}
              onChange={(eixo) => eixo && definir({ eixo })}
              allowDeselect={false}
              w={LARGURA.selecao}
            />
          </GrupoFiltros>
        )}
      </BarraFiltros>
      <PainelExclusoes
        r={r}
        officers={lista(f.excluir_officers)}
        carteiras={excluidas}
        definir={(mudancas) => {
          setUltima(null);
          definir(mudancas);
        }}
        ultima={ultima}
        desfazer={desfazer}
      />
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {consulta.error instanceof ErroApi
            ? consulta.error.message
            : 'Não foi possível carregar a performance.'}
        </Alert>
      )}
      {consulta.isPending && <Loader />}
      {estado?.status === 'rodando' && (
        <Alert
          color="blue"
          icon={<Loader size="sm" />}
          title="Carregando o histórico do Warren One"
        >
          Iniciado em {formatarDataHora(estado.iniciado_em)} (há{' '}
          <Cronometro desde={estado.iniciado_em} />
          ). A primeira carga do dia leva cerca de 1 minuto; o resultado aparece aqui sozinho.
        </Alert>
      )}
      {estado?.status === 'erro' && (
        <Alert color="red" title="A carga falhou">
          {estado.mensagem} Tente de novo em 1 minuto.
        </Alert>
      )}
      {r?.aviso && <Alert color="yellow">{r.aviso}</Alert>}
      {r && !r.aviso && r.kpis && (
        <>
          <SimpleGrid cols={{ base: 2, md: 4, xl: r.kpis.length }}>
            {r.kpis.map((k) => (
              <Kpi key={k.rotulo} kpi={k} />
            ))}
          </SimpleGrid>
          {r.resumo && (
            <Secao
              titulo={`Resumo por ${NOME_AGRUPAMENTO[agrupamentoMostrado]}`}
              legenda={legendaResumo(r)}
            >
              <TabelaDados tabela={r.resumo} alturaAutomatica />
            </Secao>
          )}
          {r.grafico && (
            <Secao
              titulo={r.agrupar === 'cluster' ? `Retorno × ${rotuloEixo}` : 'Retorno por carteira'}
              legenda={
                r.agrupar === 'cluster'
                  ? `Uma cor por grupo de pares${r.benchmarks?.caixa ? `; a linha tracejada é o ${r.benchmarks.caixa}` : ''}. Carteiras sem características ficam fora do gráfico.`
                  : r.benchmarks?.caixa
                    ? `Ordenado do maior para o menor; a linha tracejada é o ${r.benchmarks.caixa}.`
                    : 'Ordenado do maior para o menor.'
              }
            >
              <Grafico grafico={r.grafico} />
            </Secao>
          )}
          {r.tabela && (
            <Secao
              titulo="Carteiras"
              legenda={[
                r.parcial
                  ? 'Mês em curso: acumulado até a última posição de cada carteira (coluna Até).'
                  : 'Rentabilidade do mês publicada pelo One.',
                'Desvio contra a mediana do grupo inteiro, antes do filtro de officer.',
                'Clique numa linha para excluir a carteira do cálculo.',
                r.caracteristicas
                  ? 'Alocação e patrimônio na posição mais recente.'
                  : 'Patrimônio na posição mais recente.',
              ].join(' ')}
            >
              <TabelaDados tabela={r.tabela} altura={520} aoClicarLinha={excluirPelaTabela} />
            </Secao>
          )}
        </>
      )}
    </Pagina>
  );
}
